import { useCallback, useEffect, useState } from "react";
import { EnvelopeSimple, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { JobEmailDialog } from "@/components/JobEmailDialog";

const shortTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

const StatusBadge = ({ status, error, testId }) => {
  const failed = status === "failed";
  return (
    <span
      data-testid={testId}
      title={failed ? error || "Delivery failed" : "Accepted by the mail provider"}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${
        failed
          ? "border border-red-500/40 bg-red-500/10 text-red-300"
          : "border border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
      }`}
    >
      {failed ? (
        <WarningCircle size={11} weight="bold" />
      ) : (
        <CheckCircle size={11} weight="bold" />
      )}
      {failed ? "Failed" : "Sent"}
    </span>
  );
};

export const JobEmailThread = ({
  jobIds = [],
  composeJobId,
  testId = "job-email-thread",
}) => {
  const [emails, setEmails] = useState([]);
  const [composeOpen, setComposeOpen] = useState(false);
  const ids = (jobIds || []).filter(Boolean);
  const key = ids.join(",");

  const load = useCallback(async () => {
    if (!ids.length) {
      setEmails([]);
      return;
    }
    try {
      const res = await Promise.all(
        ids.map((jid) => api.get(`/jobs/${jid}/emails`)),
      );
      const merged = {};
      res.forEach(({ data }) =>
        (data.items || []).forEach((em) => {
          merged[em.id] = em;
        }),
      );
      const list = Object.values(merged).sort((a, b) =>
        (b.sent_at || "").localeCompare(a.sent_at || ""),
      );
      setEmails(list);
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="mt-3 space-y-2" data-testid={testId}>
      {composeJobId && (
        <div className="flex justify-end">
          <button
            type="button"
            data-testid={`${testId}-compose`}
            onClick={() => setComposeOpen(true)}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-violet-500/50 bg-violet-500/10 px-3 text-[11px] font-bold uppercase tracking-widest text-violet-200 transition-colors duration-150 hover:bg-violet-500/20"
          >
            <EnvelopeSimple size={14} weight="bold" /> Email customer
          </button>
        </div>
      )}
      {emails.length === 0 && (
        <p className="text-sm text-zinc-500">No emails sent yet.</p>
      )}
      {emails.map((em, i) => (
        <div
          key={em.id || i}
          data-testid={`${testId}-item-${i}`}
          className="rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2.5"
        >
          <div className="flex items-center gap-2">
            <div className="flex min-w-0 flex-1 items-center gap-1.5 text-[13px] font-semibold text-violet-300">
              <EnvelopeSimple size={13} weight="bold" className="shrink-0" />
              <span className="truncate">{em.subject}</span>
            </div>
            <StatusBadge
              status={em.status}
              error={em.error}
              testId={`${testId}-status-${i}`}
            />
          </div>
          {em.body && (
            <p className="mt-1 whitespace-pre-line text-xs text-zinc-400">
              {em.body}
            </p>
          )}
          {em.status === "failed" && em.error && (
            <p
              data-testid={`${testId}-error-${i}`}
              className="mt-1 text-[11px] text-red-300"
            >
              {em.error}
            </p>
          )}
          <div className="mt-1.5 text-[10px] uppercase tracking-widest text-zinc-500">
            To {em.to} · {shortTime(em.sent_at)}
            {em.sent_by ? ` · ${em.sent_by}` : ""}
          </div>
        </div>
      ))}
      {composeJobId && (
        <JobEmailDialog
          open={composeOpen}
          onOpenChange={setComposeOpen}
          jobId={composeJobId}
          onSent={load}
        />
      )}
    </div>
  );
};

export default JobEmailThread;
